import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Warning, Package, ShareNetwork, Key, EnvelopeSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { itApi } from './itApi';
import { todayJst } from '@/utils/jstDate';

const EMAIL_ACTIONS = [
  { value: 'suspend', label: 'メールを停止（データは保持）' },
  { value: 'reassign', label: '別の社員へ引継ぎ' },
  { value: 'none', label: '何もしない' },
];

const ToggleRow = ({ icon: Icon, label, sub, checked, onChange, count, testid }) => (
  <label className="flex items-center justify-between gap-3 p-3.5 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50 transition-colors" data-testid={testid}>
    <span className="flex items-center gap-3 min-w-0">
      <Icon size={20} weight="duotone" className="text-zinc-500 shrink-0" />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-zinc-800">{label} <span className="text-zinc-400 font-normal">（{count}件）</span></span>
        {sub && <span className="block text-[11px] text-zinc-400">{sub}</span>}
      </span>
    </span>
    <Checkbox checked={checked} onCheckedChange={onChange} disabled={count === 0} />
  </label>
);

export default function OffboardingDialog({ open, onClose, profile, staffList, onDone }) {
  const [returnAssets, setReturnAssets] = useState(true);
  const [releaseIps, setReleaseIps] = useState(true);
  const [releaseSeats, setReleaseSeats] = useState(true);
  const [emailAction, setEmailAction] = useState('suspend');
  const [reassignTo, setReassignTo] = useState('');
  const [setStatus, setSetStatus] = useState(true);
  const [leaveDate, setLeaveDate] = useState(() => todayJst());
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReturnAssets(true); setReleaseIps(true); setReleaseSeats(true);
      setEmailAction('suspend'); setReassignTo(''); setSetStatus(true);
      setLeaveDate(todayJst()); setConfirmText('');
    }
  }, [open]);

  if (!profile) return null;
  const counts = {
    assets: profile.assets.length,
    ips: profile.ips.length,
    seats: profile.license_seats.length,
    emails: profile.emails.length,
  };
  const others = (staffList || []).filter((s) => s.id !== profile.id && s.status === 'active');
  const canConfirm = confirmText.trim().toUpperCase() === 'CONFIRM' && !busy
    && !(emailAction === 'reassign' && counts.emails > 0 && !reassignTo);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await itApi.offboard(profile.id, {
        return_assets: returnAssets,
        release_ips: releaseIps,
        release_seats: releaseSeats,
        email_action: emailAction,
        reassign_to: emailAction === 'reassign' ? reassignTo : null,
        set_status: setStatus,
        leave_date: setStatus ? leaveDate : null,
      });
      const s = res.summary || {};
      toast.success(`オフボーディング完了：資産${s.assets_returned || 0} / IP${s.ips_released || 0} / 座席${s.seats_released || 0} / メール${s.emails || 0}`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'オフボーディングに失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0 gap-0" aria-describedby={undefined} data-testid="offboard-dialog">
        <div className="bg-rose-50 px-6 py-4 rounded-t-lg border-b border-rose-100 flex items-center gap-3">
          <Warning size={22} weight="fill" className="text-rose-500 shrink-0" />
          <DialogHeader className="space-y-0">
            <DialogTitle className="text-rose-700">オフボーディング：{profile.name}</DialogTitle>
            <p className="text-xs text-rose-600/80 mt-0.5">退職処理として、保有リソースを一括で解除します。</p>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-3">
          <ToggleRow icon={Package} label="資産を返却" sub="在庫に戻し、貸出履歴に記録" count={counts.assets} checked={returnAssets} onChange={setReturnAssets} testid="offboard-toggle-assets" />
          <ToggleRow icon={ShareNetwork} label="IPアドレスを解放" sub="使用中IPを空きに戻す" count={counts.ips} checked={releaseIps} onChange={setReleaseIps} testid="offboard-toggle-ips" />
          <ToggleRow icon={Key} label="ライセンス座席を解放" sub="割当を削除し座席を空ける" count={counts.seats} checked={releaseSeats} onChange={setReleaseSeats} testid="offboard-toggle-seats" />

          <div className="p-3.5 border border-zinc-200 rounded-lg space-y-2.5" data-testid="offboard-email-block">
            <span className="flex items-center gap-3 text-sm font-medium text-zinc-800">
              <EnvelopeSimple size={20} weight="duotone" className="text-zinc-500" />
              メールアカウント <span className="text-zinc-400 font-normal">（{counts.emails}件）</span>
            </span>
            <Select value={emailAction} onValueChange={setEmailAction}>
              <SelectTrigger data-testid="offboard-email-action" className="h-10" disabled={counts.emails === 0}><SelectValue /></SelectTrigger>
              <SelectContent>
                {EMAIL_ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {emailAction === 'reassign' && counts.emails > 0 && (
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger data-testid="offboard-reassign-select" className="h-10"><SelectValue placeholder="引継ぎ先の社員を選択" /></SelectTrigger>
                <SelectContent>
                  {others.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <label className="flex items-center justify-between gap-3 p-3.5 border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50 transition-colors" data-testid="offboard-toggle-status">
            <span className="text-sm font-medium text-zinc-800">在籍ステータスを「退職」にする</span>
            <Checkbox checked={setStatus} onCheckedChange={setSetStatus} />
          </label>
          {setStatus && (
            <div className="pl-1">
              <Label className="text-xs font-semibold text-zinc-600">退職日</Label>
              <DateField value={leaveDate} onChange={(v) => setLeaveDate(v)} className="mt-1" inputClassName="h-10" testId="offboard-leave-date" />
            </div>
          )}

          <div className="pt-1">
            <Label className="text-xs font-semibold text-zinc-600">確認のため <span className="font-mono text-rose-600">CONFIRM</span> と入力してください</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="CONFIRM" className="h-10 mt-1 font-mono" data-testid="offboard-confirm-input" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="offboard-cancel-btn">キャンセル</Button>
            <Button disabled={!canConfirm} onClick={submit} data-testid="offboard-confirm-btn" className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">
              {busy ? '処理中…' : '実行する'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
